// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

/* eslint-disable spire/fenced-imports */
import FancyScroll from "@adl/App/Components/Common/FancyScroll";
import LazyTippy from "@adl/App/Components/Common/LazyTippy";
import XngZone from "@adl/App/Components/Common/XngZone";
import { xHeaderUpdate } from "@adl/App/Handlers/XNG/Header";
import { FeaturedProductsWidgetStyles } from "@adl/App/Widgets/FeaturedProductsWidget/FeaturedProductsWidget";
import { DivWrapper } from "@adl/XDL/constants";
import breakpoints from "@adl/XDL/PreStyle/breakpoints";
import { buttonTransparent } from "@adl/XDL/PreStyle/button";
import { color, styledScrollbar } from "@adl/XDL/Utilities";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { createWidgetElement } from "@insite/client-framework/Components/ContentItemStore";
import translate from "@insite/client-framework/Translate";
import Button from "@insite/mobius/Button";
import Clickable, { ClickablePresentationProps, ClickableProps, StyledButton } from "@insite/mobius/Clickable";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import { IconMemo, IconPresentationProps, IconWrapper } from "@insite/mobius/Icon";
import ChevronRight from "@insite/mobius/Icons/ChevronRight";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps, TypographyProps } from "@insite/mobius/Typography";
import applyPropBuilder from "@insite/mobius/utilities/applyPropBuilder";
import getColor from "@insite/mobius/utilities/getColor";
import getProp from "@insite/mobius/utilities/getProp";
import InjectableCss, { StyledProp } from "@insite/mobius/utilities/InjectableCss";
import injectCss from "@insite/mobius/utilities/injectCss";
import MobiusStyledComponentProps from "@insite/mobius/utilities/MobiusStyledComponentProps";
import uniqueId from "@insite/mobius/utilities/uniqueId";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import React from "react";
import { connect, ResolveThunks } from "react-redux";
import styled, { css, ThemeConsumer, ThemeProps, withTheme } from "styled-components";
import { Instance } from "tippy.js";

const mapDispatchToProps = {
    xHeaderUpdate,
};

export interface MappedLink {
    // expanded from content-library/src/widgets/header/MainNavigation.tsx
    title: string;
    url?: string;
    type?: string;
    clickableProps?: ClickableProps;
    openInNewWindow?: boolean;
    numberOfColumns?: number;
    maxDepth?: number;
    childrenType?: "MegaMenu" | "CascadingMenu";
    children?: MappedLink[];
    excludeFromNavigation?: boolean;
    iconProps?: IconPresentationProps;
    onClick?: () => void;
}

// <XNG-Changes>
export interface MenuLevel2FullWidth {
    wrapper?: InjectableCss;
    conentWrapper?: InjectableCss;
    titleWrapper?: InjectableCss;
    title?: TypographyProps;
    shopAllLink?: LinkPresentationProps;
    linksWrapper?: InjectableCss;
    linkWrapper?: InjectableCss;
    link?: LinkPresentationProps;
    featuredProductsWrapper?: InjectableCss;
    featuredProducts?: FeaturedProductsWidgetStyles;
    solutionsWidgetWrapper?: InjectableCss;
    scrollWrapper?: InjectableCss;
}
// <XNG-Changes>

export interface MenuPresentationProps {
    /**
     * Indicates how the `css` property is combined with the variant `css` property from the theme.
     * If true, the variant css is applied first and then the component css is applied after causing
     * a merge, much like normal CSS. If false, only the component css is applied, overriding the variant css in the theme.
     */
    mergeCss?: boolean;
    /** CSS string or styled-components function to be injected into this component
     * @themable */
    cssOverrides?: {
        wrapper?: StyledProp<MenuPresentationProps>;
        menu?: StyledProp<MenuPresentationProps>;
        menuItem?: StyledProp<MenuPresentationProps>;
    };
    /** Props passed to the menu items.
     * @themable */
    menuItemTypographyProps?: TypographyPresentationProps;
    /** Props passed to the menu item Clickable.
     * @themable */
    menuItemClickableProps?: ClickablePresentationProps;
    /** Props to be passed to the right chevron arrow for menu items with children.
     * @themable */
    moreIconProps?: IconPresentationProps;
    // XNG-Changes
    menueLevel2FullWidth?: MenuLevel2FullWidth;
}

type MenuComponentProps = MobiusStyledComponentProps<
    "nav",
    {
        /** Aria attributes used internally require an `id`. If not provided, a random id is generated. */
        descriptionId?: string;
        /** Maximum number of menus that will appear through submenu cascading. */
        maxDepth?: number;
        /** The nested child structure that will be rendered by the menu. */
        menuItems: MappedLink[];
        /** The element that triggers the menu. */
        menuTrigger: React.ReactElement;
        /** Width of the menu and submenus in pixels. */
        width?: number;
        /** Allows forcing the state of this menu to be open */
        isOpen?: true;
        /** Session boolean value that decides whether to show Change Customer Link */
        displayChangeCustomerLink?: boolean;
        // XNG-Changes
        trigger?: string;
        /** To show Menu from begging, x=0 */
        showAsFullWidth?: boolean;
    }
>;

/** MenuComponentProps with defaulted properties now listed as not-optional, plus theme. */
export type MenuProps = MenuComponentProps &
    ResolveThunks<typeof mapDispatchToProps> &
    MenuPresentationProps &
    ThemeProps<BaseTheme> & {
        widgetId?: string;
        productsTitle?: string;
        solutionsWidget?: any;
    };

// <XNG-Changes>
const menueLevel2FullWidthStyles: MenuLevel2FullWidth = {
    wrapper: {
        css: css`
            width: 100%;
            max-height: 600px;
            position: relative;
        `,
    },
    conentWrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            padding: 8px 24px 20px;
            overflow-y: auto;
        `,
    },
    scrollWrapper: {
        css: css`
            overflow: auto;
            ${styledScrollbar(8)};
            > div {
                max-height: 100%;
            }
        `,
    },
    titleWrapper: {
        css: css`
            display: flex;
            align-items: flex-end;
            flex-wrap: wrap;
            width: 100%;
            margin-bottom: 16px;
        `,
    },
    title: {
        variant: "h2",
        css: css`
            margin-bottom: 0;
        `,
    },
    shopAllLink: {
        color: "primary",
        typographyProps: {
            size: 15,
            weight: 600,
            css: css`
                letter-spacing: 0;
                line-height: 26px;
                text-decoration: underline;
                &:focus {
                    outline: none;
                }
            `,
        },
        css: css`
            text-decoration: underline;
            margin-left: 36px;
        `,
    },
    linksWrapper: {
        css: css`
            display: flex;
            align-items: center;
            flex-wrap: wrap;
        `,
    },
    linkWrapper: {
        css: css`
            flex-basis: calc(100% / 3);
            > a {
                width: 100%;
            }
        `,
    },
    link: {
        color: "text.main",
        typographyProps: {
            size: 15,
            weight: "normal",
            css: css`
                letter-spacing: 0;
                line-height: 20px;
            `,
        },
        css: css`
            margin-bottom: 16px;
            &:focus {
                outline: none;
            }
        `,
    },
    featuredProductsWrapper: {
        css: css`
            padding: 24px 24px 0;
            border-top: 1px solid ${color("common.border")};
            background-color: ${color("common.white")};
        `,
    },
    solutionsWidgetWrapper: {
        css: css`
            padding: 22px 48px;
        `,
    },
    featuredProducts: {
        title: {
            variant: "h4",
            color: "text",
            css: css`
                margin-bottom: 16px;
            `,
        },
    },
};
// </XNG-Changes>

// eslint-disable-next-line no-unexpected-multiline
const MenuStyle = styled.ul<
    ThemeProps<BaseTheme> &
        InjectableCss & {
            width?: number;
        }
>`
    background: ${getColor("common.white")};
    width: ${getProp("width", 175)}px;
    max-width: 25vw;
    height: 600px;
    padding: 16px 0;
    list-style: none;
    margin: 0;
    overflow: auto;
    border: 1px solid ${getColor("common.ui14")};
    overscroll-behavior: contain;
    ${styledScrollbar(8)};
    &.full-width {
        max-width: unset;
        > ${DivWrapper} {
            padding: 22px 48px;
        }
    }

    display: flex;
    flex-direction: column;

    ${injectCss}
`;

const MenuItemText = styled(Typography as any)<{ hasChildren?: boolean }>`
    color: ${getColor("text")};
    display: inline;
    box-sizing: border-box;
    font-weight: normal;
    font-size: 16px;
    letter-spacing: 0;
    line-height: 26px;
`;

// eslint-disable-next-line no-unexpected-multiline
const MenuItemStyle = styled.div<
    ThemeProps<BaseTheme> &
        InjectableCss & {
            width?: number;
        }
>`
    position: relative;
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0 8px;
    &:focus-within > ${MenuStyle} {
        display: block;
        left: ${getProp("width", 175)}px;
        top: 0;
    }
    &:focus > ${MenuStyle}, &:hover > ${MenuStyle}, &.focus-within > ${MenuStyle} {
        display: block;
        left: ${getProp("width", 175)}px;
        top: 0;
    }

    &:focus {
        outline: none;
    }

    ${StyledButton} {
        display: inline-block;
        font-weight: bold;
        padding: 8px;
        transition: background ease-in-out 250ms;
    }

    ${MenuItemText} {
        transition: color ease-in-out 250ms;
        color: ${({ hasLastChanceLine }: any) => (hasLastChanceLine ? getColor("danger") : "")};
    }
    ${IconWrapper} {
        display: none;
    }
    &:hover {
        background: ${color("common.ui1", 0.25)};
        ${MenuItemText} {
            color: ${({ hasLastChanceLine }: any) => (hasLastChanceLine ? getColor("danger") : getColor("text.main"))};
        }
    }

    &[aria-expanded="true"] {
        background: ${color("common.ui1", 0.25)};

        & > ${StyledButton}, ${StyledButton}:hover, ${StyledButton}:focus {
            ${MenuItemText} {
                color: ${getColor("text.main")};
            }
        }
    }
    a${StyledButton}:hover > ${MenuItemText},a${StyledButton}:focus > ${MenuItemText} {
        color: ${({ hasLastChanceLine }: any) => (hasLastChanceLine ? getColor("danger") : getColor("text.main"))};
    }

    ${injectCss}
`;

const MenuTrigger = styled.span<InjectableCss<any>>`
    .trigger-item,
    .arrow {
        transition: transform ease-in-out 250ms;
        color: ${color("common.white")};
    }

    &:hover {
        .trigger-item {
            background: ${color("common.ui2")};
        }
        .arrow {
            color: ${color("common.white")};
        }
    }

    &[aria-expanded="true"] {
        .arrow {
            transform: rotate(180deg);
        }
        .trigger-item {
            background: ${color("common.ui2")};
        }
    }
`;

const hideOnPopperBlur = {
    name: "hideOnPopperBlur",
    defaultValue: true,
    fn(instance: Instance) {
        return {
            onCreate() {
                instance.popper.addEventListener("focusout", event => {
                    if (
                        event.relatedTarget &&
                        event.relatedTarget instanceof HTMLElement &&
                        !instance.popper.contains(event.relatedTarget)
                    ) {
                        instance.hide();
                    }
                });
            },
        };
    },
};

const ShowMenu = () => {
    return (
        <Button
            color="transparent"
            css={css`
                padding: 0;
                transform: translateY(-50%);
                min-height: initial;
                display: inline-flex;
                align-items: center;
                &,
                & ${IconWrapper} {
                    display: none;
                }
                ${buttonTransparent}
            `}
        >
            <IconMemo src={ChevronRight} size={15} />
            <VisuallyHidden>{translate("Open Menu")}</VisuallyHidden>
        </Button>
    );
};

interface MenuState {
    canBeOpen: boolean;
    shouldRender: boolean;
}

/**
 * A cascading menu that renders a defined number of sub-menus.
 *
 * You should add the code to Header page file:
 *   // Hide main navigation
 *   useEffect(() => {
 *       setTimeout(() => hideAll());
 *   }, [isSticky, currentLocation]);
 */
class Menu extends React.Component<MenuProps, MenuState> {
    menuWrapperRef = React.createRef<HTMLElement>();

    static defaultProps: Partial<MenuProps> = {
        maxDepth: 3,
        width: 325,
        trigger: "mouseenter focusin",
    };

    state: MenuState = {
        canBeOpen: true,
        shouldRender: true,
    };

    closeMenu = () => {
        this.setState({ canBeOpen: false }, () =>
            setTimeout(() => {
                // The make the all browsers act the same on item click.
                if (document.activeElement instanceof HTMLElement) {
                    document.activeElement.blur();
                }
                this.setState({ canBeOpen: true });
            }, 100),
        );
    };

    closeMenuWithoutChildren = (hasMenuChildren?: boolean) => {
        // This is for high resolution mobile users.
        // So that the menu closes on nav items without children
        if (!hasMenuChildren) {
            this.setState(
                {
                    shouldRender: false,
                },
                () => {
                    this.setState({ shouldRender: true });
                },
            );
        }
    };

    menuWrapperMouseLeaveHandler = () => {
        if (!this.menuWrapperRef.current) {
            return;
        }
        this.menuWrapperRef.current.blur();
        const menuTrigger = this.menuWrapperRef.current.children[0] as HTMLElement;
        if (!menuTrigger) {
            return;
        }
        menuTrigger.blur();
    };

    render() {
        const { showAsFullWidth, solutionsWidget } = this.props;
        return (
            <ThemeConsumer>
                {theme => {
                    const {
                        menuItems,
                        menuTrigger,
                        isOpen,
                        mergeCss,
                        xHeaderUpdate,
                        widgetId,
                        productsTitle,
                        ...otherProps
                    } = this.props;
                    const { applyProp, spreadProps, applyStyledProp } = applyPropBuilder(
                        { ...otherProps, theme },
                        { component: "menu" },
                    );
                    const resolvedMergeCss = mergeCss ?? otherProps?.theme?.menu?.defaultProps?.mergeCss;
                    const renderMenuItems = (
                        parentDepth: number,
                        childMenuItems: (MappedLink & { id?: string })[],
                        currentDepth: number,
                        title?: string,
                        url?: string,
                        id?: string,
                    ) => {
                        const cssOverrides = spreadProps("cssOverrides");
                        const parentMenu = React.createRef<HTMLUListElement>();
                        const parentDepthNum = Number(parentDepth || 0);
                        const maxDepthOne = parentDepthNum === 1 && currentDepth === 1;
                        const maxDepthTwo = parentDepthNum === 2 && currentDepth === 2;
                        const showMenuFullWidth = maxDepthOne || (maxDepthTwo && showAsFullWidth);
                        const { maxWidths, paddingValues } = breakpoints as any;
                        const pageInnerWidth =
                            maxWidths[maxWidths.length - 1] - paddingValues[paddingValues.length - 1] * 2;
                        // XNG-Changes
                        const contentWrapper = React.createRef<HTMLDivElement>();
                        if (maxDepthOne && showAsFullWidth) {
                            return (
                                <StyledWrapper {...menueLevel2FullWidthStyles.wrapper}>
                                    <FancyScroll refElement={parentMenu}>
                                        <MenuStyle
                                            width={pageInnerWidth}
                                            css={applyStyledProp(["cssOverrides", "menu"], resolvedMergeCss)}
                                            ref={parentMenu}
                                            className="full-width"
                                        >
                                            {widgetId && (
                                                <XngZone
                                                    contentId={widgetId}
                                                    zoneName="Solutions"
                                                    hint="Zone For Solutions"
                                                />
                                            )}

                                            {solutionsWidget ? (
                                                <StyledWrapper {...menueLevel2FullWidthStyles.solutionsWidgetWrapper}>
                                                    {createWidgetElement("SolutionWidget/SolutionsWidget", {
                                                        fields: { ...solutionsWidget },
                                                    })}
                                                </StyledWrapper>
                                            ) : null}
                                        </MenuStyle>
                                    </FancyScroll>
                                </StyledWrapper>
                            );
                        }
                        if (showMenuFullWidth) {
                            return (
                                <StyledWrapper {...menueLevel2FullWidthStyles.wrapper}>
                                    <MenuStyle
                                        width={maxDepthOne ? pageInnerWidth : pageInnerWidth - (otherProps.width || 0)}
                                        css={cssOverrides?.menu}
                                        ref={parentMenu}
                                        className="full-width"
                                    >
                                        <StyledWrapper
                                            ref={contentWrapper}
                                            {...menueLevel2FullWidthStyles.scrollWrapper}
                                        >
                                            <FancyScroll refElement={contentWrapper}>
                                                <StyledWrapper {...menueLevel2FullWidthStyles.conentWrapper}>
                                                    <StyledWrapper {...menueLevel2FullWidthStyles.titleWrapper}>
                                                        <Typography {...menueLevel2FullWidthStyles.title}>
                                                            {title}
                                                        </Typography>
                                                        <Link href={url} {...menueLevel2FullWidthStyles.shopAllLink}>
                                                            {translate("Shop All")}
                                                        </Link>
                                                    </StyledWrapper>
                                                    <StyledWrapper {...menueLevel2FullWidthStyles.linksWrapper}>
                                                        {childMenuItems
                                                            ?.filter(item => !item.excludeFromNavigation)
                                                            .map(
                                                                link =>
                                                                    link?.url && (
                                                                        <StyledWrapper
                                                                            key={`${link?.title}_${link?.url}`}
                                                                            {...menueLevel2FullWidthStyles.linkWrapper}
                                                                        >
                                                                            <Link
                                                                                href={link?.url}
                                                                                {...menueLevel2FullWidthStyles.link}
                                                                            >
                                                                                {link?.title}
                                                                            </Link>
                                                                        </StyledWrapper>
                                                                    ),
                                                            )}
                                                    </StyledWrapper>
                                                </StyledWrapper>
                                            </FancyScroll>
                                        </StyledWrapper>
                                        <StyledWrapper {...menueLevel2FullWidthStyles.featuredProductsWrapper}>
                                            {createWidgetElement("FeaturedProductsWidget/FeaturedProductsWidget", {
                                                fields: {
                                                    title: productsTitle,
                                                    categoryId: id,
                                                },
                                                extendedStyles: menueLevel2FullWidthStyles.featuredProducts,
                                            } as any)}
                                        </StyledWrapper>
                                    </MenuStyle>
                                </StyledWrapper>
                            );
                        }
                        return (
                            <FancyScroll refElement={parentMenu}>
                                <MenuStyle width={otherProps.width} css={cssOverrides?.menu} ref={parentMenu}>
                                    {childMenuItems
                                        ?.filter(item => !item.excludeFromNavigation)
                                        .map((item, index) => {
                                            if (
                                                (item.type === "ChangeCustomerPage" &&
                                                    !this.props.displayChangeCustomerLink) ||
                                                !this.state.shouldRender
                                            ) {
                                                return null;
                                            }
                                            const maxDepth = +(item.maxDepth || parentDepth);
                                            const hasChildren =
                                                item.children?.some(link => !link.excludeFromNavigation) &&
                                                currentDepth < maxDepth;
                                            const menuItem = (
                                                <MenuItemStyle
                                                    width={otherProps.width}
                                                    css={applyStyledProp(
                                                        ["cssOverrides", "menuItem"],
                                                        resolvedMergeCss,
                                                    )}
                                                    onClick={() => {
                                                        this.closeMenuWithoutChildren(hasChildren);
                                                    }}
                                                    // XNG-Chnage
                                                    {...{
                                                        hasLastChanceLine: item.id === "last_chance_page_link",
                                                    }}
                                                >
                                                    <Clickable
                                                        href={item.url}
                                                        onClick={() => {
                                                            item.onClick?.();
                                                            this.closeMenu();
                                                        }}
                                                        {...applyProp("menuItemClickableProps")}
                                                        {...item.clickableProps}
                                                    >
                                                        {item.iconProps && <IconMemo {...item.iconProps} />}
                                                        <MenuItemText
                                                            {...applyProp("menuItemTypographyProps")}
                                                            data-test-selector="menuItemText"
                                                        >
                                                            {item.title}
                                                        </MenuItemText>
                                                    </Clickable>
                                                    {hasChildren && <ShowMenu></ShowMenu>}
                                                </MenuItemStyle>
                                            );
                                            return (
                                                <li key={`${item.title}_${index}`}>
                                                    {!hasChildren && menuItem}
                                                    {hasChildren && (
                                                        <LazyTippy
                                                            render={() =>
                                                                renderMenuItems(
                                                                    maxDepth,
                                                                    item!.children!,
                                                                    currentDepth + 1,
                                                                    item.title,
                                                                    item.url,
                                                                    item.id,
                                                                )
                                                            }
                                                            trigger="mouseenter click"
                                                            placement="right-start"
                                                            interactive={true}
                                                            animation={false}
                                                            offset={[0, -1]}
                                                            delay={[100, 350]}
                                                            touch={item.url ? "hold" : true}
                                                            plugins={[hideOnPopperBlur]}
                                                            getReferenceClientRect={() =>
                                                                parentMenu.current!.getBoundingClientRect()
                                                            }
                                                            popperOptions={{
                                                                modifiers: [
                                                                    {
                                                                        name: "preventOverflow",
                                                                        options: { mainAxis: false },
                                                                    },
                                                                ],
                                                            }}
                                                        >
                                                            {menuItem}
                                                        </LazyTippy>
                                                    )}
                                                </li>
                                            );
                                        })}
                                </MenuStyle>
                            </FancyScroll>
                        );
                    };

                    const cssOverrides = spreadProps("cssOverrides");
                    const descriptionId = otherProps.descriptionId || uniqueId();
                    const currentDepth = 1;
                    const appendToElement = showAsFullWidth
                        ? () => document.getElementById("main-navigation-container") || document.body
                        : "parent";
                    return (
                        <>
                            <LazyTippy
                                render={() => <>{renderMenuItems(otherProps.maxDepth || 0, menuItems, currentDepth)}</>}
                                trigger={this.props.trigger}
                                placement="bottom-start"
                                interactive={true}
                                animation={false}
                                delay={[350, 500]}
                                offset={[0, 0]}
                                plugins={[hideOnPopperBlur]}
                                touch={["hold", 250]}
                                appendTo={appendToElement}
                                onShow={instance => {
                                    xHeaderUpdate({ key: "registerMenu", value: `${instance.id}_menu` });
                                }}
                                onHidden={instance => {
                                    xHeaderUpdate({ key: "unregisterMenu", value: `${instance.id}_menu` });
                                }}
                                popperOptions={{
                                    modifiers: [
                                        {
                                            name: "arrow",
                                            options: {
                                                padding: 0,
                                                element: ".menu-arrow",
                                            },
                                        },
                                        {
                                            name: "computeStyles",
                                            options: {
                                                roundOffsets: ({ x, y }: any) => ({
                                                    x: showAsFullWidth ? 0 : x,
                                                    y,
                                                }),
                                            },
                                        },
                                    ],
                                }}
                            >
                                <MenuTrigger>
                                    {React.cloneElement(menuTrigger, {
                                        id: descriptionId,
                                        className: `trigger trigger-item`,
                                    })}
                                </MenuTrigger>
                            </LazyTippy>
                        </>
                    );
                }}
            </ThemeConsumer>
        );
    }
}

/** @component */
export default connect(null, mapDispatchToProps)(withTheme(Menu));

export { MenuItemText };
